import React, { FC, useEffect, useMemo, useState } from "react";
import { Elements, useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import {
    Button,
    CircularProgress,
    Stack,
    styled,
    Typography,
    useTheme
} from "@mui/material";
import { PaymentClient } from "../../../../utilities/backend/payment";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../utilities/redux/store";
import { UsersClient } from "../../../../utilities/backend/client";

//export const stripePromise = loadStripe("pk_test_51QsTOLQVGhP1Aa1rppqGGyGiq9rpFS7qdQgpT42HH3crEVZpEhy5OjCfWRREPUkzjXONPshcQWf0ECsLUk2Kticz00BYU1mP7m"); // For QA environment
export const stripePromise = loadStripe("pk_live_51NcxeqHd6MPELUNLcqH8bV05ckKDfSYE5iUNXguBT7qT2o78Hsx48MS4YzN3hOoAyM4ZNn9x3TXAr2uCMBEYh1YX00E3g0Chfy"); // For production environment

const Wrapper = styled(Stack)(({ theme }) => ({
    paddingTop: "40px",
    margin: "auto",
    width: "100%",  // Ensure it takes full width
    maxWidth: "1200px",
    display: "flex",
    justifyContent: "flex-end", // Move form to the right
    paddingRight: "40px", // Add padding for spacing
    [theme.breakpoints.down("sm")]: {
        width: "initial",
        padding: "40px 17px 80px",
        justifyContent: "center", // Center on smaller screens
        paddingRight: "0px",
    },
}));

const ErrorMessage = styled("div")(({ theme }) => ({
    width: "100%",
    maxWidth: "500px",
    padding: "12px",
    color: "#d32f2f",
    fontFamily: "sans-serif",
    marginBottom: "16px",
    textAlign: "center",
    wordWrap: "break-word",
}));

const FormContainer = styled("div")(({ theme }) => ({
    width: "400px", // Set a fixed width for the form
    [theme.breakpoints.down("sm")]: {
        width: "100%", // Make it full-width on small screens
    },
}));

const ACHPaymentForm: FC<{ redirectUrl: string; email: string; fullName: string }> = ({ redirectUrl, email, fullName }) => {

    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const theme = useTheme();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);

        localStorage.setItem("ach-payment-redirectUrl", redirectUrl);

        const { error } = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: `${window.location.origin}/ach-payment-success` },
        });

        if (error) {
            setMessage(error.message || "Payment failed.");
        } else {
            setMessage("Payment successful!");
        }

        setLoading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={{ defaultValues: { billingDetails: { email, name: fullName } } }} />
            <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                    mt: 2,
                    bgcolor: "#80B73F",
                    color: "#FFF",
                    padding: "10px 20px",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#9DCF5F" },
                    [theme.breakpoints.down("sm")]: {
                        width: "346px",
                    },
                }}
            >
                {loading ? <CircularProgress size={23} color="inherit" /> : "Pay with ACH"}
            </Button>
            {message && <Typography color="error" mt={2}>{message}</Typography>}
        </form>
    );
};

const ACHPayment: FC = () => {

    const paymentClient = new PaymentClient();
    const userClient = new UsersClient();
    const storedAchAmount = localStorage.getItem("ach-investment-amount");
    const storedRedirectUrl = localStorage.getItem("ach-payment-redirectUrl");

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const location = useLocation();
    const investmentAmount = location.state?.investmentAmount ?? storedAchAmount;
    const redirectUrl = location.state?.returnUrl || storedRedirectUrl || "/find";
    const authToken = useSelector((state: RootState) => state.token);
    const iswithOutAuth = authToken.token === null;
    const theme = useTheme();

    const { email, firstName, lastName, isAnonymous } = location.state || {};

    const [defaultBillingDetails, setdefaultBillingDetails] = useState<{ email: string; fullName: string }>({ email: "", fullName: "" });

    const errorMessage = location.state?.errorMessage;

    const anonymousStates: { email: string, firstName: string, lastName: string, isAnonymous: boolean } | null = useMemo(() => {
        if (isAnonymous) {
            return {
                email, firstName, lastName, isAnonymous
            }
        }
        const storedState = localStorage.getItem("ach-payment-states");
        if (storedState) {
            return JSON.parse(storedState);
        } else {
            return null;
        }
    }, [email, firstName, lastName, isAnonymous]);

    useEffect(() => {
        let paymentData: Parameters<typeof paymentClient.getPaymentSecret>['0'] = { amount: Number(investmentAmount) };
        if (iswithOutAuth) {
            paymentData = {
                ...paymentData,
                userName: localStorage.getItem("userName") ?? undefined
            };
        }
        if (anonymousStates?.isAnonymous) {
            paymentData = { ...paymentData, ...anonymousStates };
        }
        if (investmentAmount) {
            paymentClient.getPaymentSecret(paymentData).then(response => {
                setClientSecret(response.data);
                localStorage.setItem("ach-investment-amount", investmentAmount);
            });
        }
    }, [setClientSecret, investmentAmount, iswithOutAuth, anonymousStates]);

    useEffect(() => {
        const storedData = localStorage.getItem("ach-payment-states");
        const parsedData = storedData ? JSON.parse(storedData) : {};

        // Create a new object without undefined values
        const newData = { email, firstName, lastName, isAnonymous };
        const filteredData = Object.fromEntries(
            Object.entries(newData).filter(([_, value]) => value !== undefined)
        );

        if (JSON.stringify(parsedData) !== JSON.stringify({ ...parsedData, ...filteredData })) {
            localStorage.setItem("ach-payment-states", JSON.stringify({ ...parsedData, ...filteredData }));
        }
    }, [email, firstName, lastName, isAnonymous]);

    useEffect(() => {
        if (!iswithOutAuth) {
            userClient.users_GetUser({ token: authToken.token || "" }).then(({ email, firstName, lastName }) => {
                setdefaultBillingDetails({ email: email ?? "", fullName: `${firstName} ${lastName}` });
            })
        } else if (anonymousStates) {
            setdefaultBillingDetails({ email: anonymousStates.email, fullName: `${anonymousStates.firstName} ${anonymousStates.lastName}` });
        }
    }, [iswithOutAuth, setdefaultBillingDetails, anonymousStates]);

    if (!clientSecret || !investmentAmount) return null;

    const options: StripeElementsOptions = { clientSecret };

    return (
        <Wrapper alignItems={"center"} justifyContent="center" spacing={4}>
            <Typography
                sx={{
                    fontSize: 22,
                    fontFamily: "Poppins",
                    lineHeight: 1.23,
                    fontWeight: 600,
                    color: "#20527F",
                    [theme.breakpoints.down("sm")]: {
                        fontSize: 22,
                    },
                }}
            >
                Thank you for your bank transfer or ACH
            </Typography>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <Elements stripe={stripePromise} options={options}>
                <FormContainer>
                    <ACHPaymentForm {...{ redirectUrl, ...defaultBillingDetails }} />
                </FormContainer>
            </Elements>
        </Wrapper>
    );
};

export default ACHPayment;
